import { IImageProps } from '@fluentui/react';
import { IPDefaultButton, IPImage, IPSpinner, IPSpinnerSize, IPText } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold, openExtIcon } from 'globalStyles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Config from 'services/Config/configService';
import logger from 'services/Logging/logService';
import { navigateToExternalUrl } from 'utils/url';

//How long to wait for the frame's load event before treating the preview as failed. Kept short: the loader
//covers the frame, so this is also the longest a document that IS rendering can stay hidden behind it.
const loadTimeoutMs = 8000;

interface IPreviewIFrameProps {
  src: string;
  title: string;
  //Target for the fallback's "open in web" button. Without it the fallback shows the message only.
  webUrl?: string;
  //Only fires on the frame's own error event, which browsers do NOT dispatch for navigation failures
  //(refused, 404, DNS) - see the note below. Do not build error handling that depends on it arriving.
  onError?: () => void;
}

//Preview frame with a loading overlay, and a fallback for when the frame never loads.
//
//Why the load event is used the way it is: an iframe's `load` fires cross-origin in every browser we target
//(only reading the content is blocked), and a frame refused by X-Frame-Options or CSP frame-ancestors has its
//load CANCELLED - the response is still 200 OK, so the network tab looks healthy and the console is the only
//trace. That makes a missing load event a real failure signal, which is what the timeout below reports. What
//`load` does NOT mean is "the document is on screen": SharePoint/WOPI viewers render client-side afterwards,
//and the event can fire more than once across a redirect chain. So it decides success vs failure only, and the
//frame is never hidden for longer than the timeout on the strength of it.
//
//The frame's `error` event is not a substitute: browsers do not dispatch it for a failed navigation either, so
//it is close to dead here. When it does arrive it selects the fallback straight away rather than waiting the
//timeout out, which also keeps callers that pass no onError from being left with a bare broken frame.
export const PreviewIFrame = (props: IPreviewIFrameProps) => {
  const { t } = useTranslation(['library', 'translation']);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [hasTimedOut, setHasTimedOut] = useState<boolean>(false);

  const imageFileNotAvailable: IImageProps = {
    src: `${Config.getImageURL()}/file-not-available.png`,
    imageFit: IPImageFit.contain,
    width: 96,
    height: 96,
  };

  //Every reload hands us a freshly minted preview URL, so src identity is the signal that the frame restarted
  //and both the loader and the timeout have to start over.
  useEffect(() => {
    setHasLoaded(false);
    setHasTimedOut(false);
  }, [props.src]);

  //Kept separate from the reset above so that hasLoaded can arm and disarm the timer without the reset running
  //again on every load and re-arming it. Dropping the timer once the frame reports a load matters for the log
  //line: left running it fires on a preview that rendered perfectly well and claims the opposite.
  useEffect(() => {
    if (hasLoaded) return;

    const timeout = setTimeout(() => {
      logger.debug('PreviewIFrame: frame did not report a load within the timeout');
      setHasTimedOut(true);
    }, loadTimeoutMs);

    return () => clearTimeout(timeout);
  }, [props.src, hasLoaded]);

  return (
    <IPStack verticalFill styles={{ root: { position: 'relative', minHeight: 0 } }}>
      <iframe
        title={props.title}
        allowFullScreen={true}
        referrerPolicy="no-referrer"
        //Absolute against the wrapper rather than a percentage of it: the frame sits one level deeper than a
        //bare iframe would, and stacking a second percentage height on top of the wrapper's own risks
        //collapsing it to the intrinsic iframe height (~150px), which reads as a blank preview.
        style={{ border: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        src={props.src}
        onLoad={() => setHasLoaded(true)}
        onError={() => {
          setHasTimedOut(true);
          props.onError?.();
        }}
      ></iframe>
      {!hasLoaded && (
        //Absolute, because the frame stays mounted underneath to keep loading: it fills the wrapper, so
        //anything in the normal flow would be pushed out of view instead of sitting on top of the blank frame.
        <IPStack
          verticalFill
          horizontalAlign="center"
          verticalAlign="center"
          tokens={globalStackTokensGapSmall}
          styles={{ root: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } }}
        >
          {!hasTimedOut && <IPSpinner size={IPSpinnerSize.large} />}
          {hasTimedOut && (
            //The frame is not unmounted here, so a document that still arrives clears this and shows through.
            //Deliberately not routed through the caller's iframe-load-error state: that also auto-opens the
            //document in a new browser tab, which is not wanted for a preview that is merely slow.
            <>
              <IPStack.Item>
                <IPImage {...imageFileNotAvailable} />
              </IPStack.Item>
              <IPStack.Item styles={{ root: { maxWidth: '80%' } }}>
                <IPText block styles={globalTextStylesBold}>
                  {t('library:Preview.IFrameError')}
                </IPText>
              </IPStack.Item>
              {props.webUrl && (
                <IPStack.Item styles={{ root: { maxWidth: '80%' } }}>
                  <IPDefaultButton
                    iconProps={openExtIcon}
                    onClick={() => navigateToExternalUrl(props.webUrl ?? '', undefined, '')}
                    text={t('library:PinnedLinkTabs.Commands.OpenInWeb')}
                  />
                </IPStack.Item>
              )}
            </>
          )}
        </IPStack>
      )}
    </IPStack>
  );
};
